import {GetValues, GetValues_ForSchema} from "js-vextensions";
import {TriggerSet} from "../@TriggerSet.js";
import {EffectPointer} from "../../@Shared/EffectPointer.js";
import {AddSchema} from "mobx-graphlink";
import {Setting} from "./@Shared/Setting.js";

export enum DreamFlawType {
	"omitWordInVoice" = "omitWordInVoice",
}
AddSchema("DreamFlawType", {enum: GetValues(DreamFlawType)});

export enum ImageFrameFlawType {
	"wrongNameVoiced" = "wrongNameVoiced",
	"wrongAudioClipPlayed" = "wrongAudioClipPlayed",
	"poolChanged" = "poolChanged",
	"none" = "none",
}
AddSchema("ImageFrameFlawType", {enum: GetValues(ImageFrameFlawType)});

export class EngineConfig_VR {
	constructor(data: Partial<EngineConfig_VR>) { Object.assign(this, data); }

	enabled: boolean;

	// general
	headsetOn_effect: EffectPointer;
	headsetOff_effect: EffectPointer;
	lieCatch_effect: EffectPointer;
	falseAccuse_effect: EffectPointer;

	// dream-panels
	dreams_count: number;
	dreams_flawed: number;
	dreams_events_min: number;
	dreams_events_max: number;
	dreams_flawType: DreamFlawType;

	// image-cubes
	frames_size: number;
	frames_duration: number; // in seconds
	frames_flickerOnDuration: number; // in ms
	frames_flickerOffDuration: number; // in ms
	frames_timeCreditTarget: number; // in ms
	frames_timeCreditMarkerInterval: number; // in ms
	frames_timeCreditLimitPerFrame: number; // in seconds
	frames_voice_volumeMultiplier: number;
	frames_voice_speedMultiplier: number;
	frames_imageFolder: string;
	frames_audioFolder: string;
	frames_videoFolder: string;
	frames_pools: FramePool[];

	// solver
	solver_enabled: boolean;
	solver_minFlawFinds: number;
	solver_markProgressBeforeCompletion: boolean;
	solver_maxTimeForProgress: number; // in seconds
}
AddSchema("EngineConfig_VR", {
	properties: {
		enabled: {type: "boolean"},

		headsetOn_effect: {$ref: "EffectPointer"},
		headsetOff_effect: {$ref: "EffectPointer"},
		lieCatch_effect: {$ref: "EffectPointer"},
		falseAccuse_effect: {$ref: "EffectPointer"},

		dreams_count: {type: "number"},
		dreams_flawed: {type: "number"},
		dreams_events_min: {type: "number"},
		dreams_events_max: {type: "number"},
		dreams_flawType: {$ref: "DreamFlawType"},

		frames_size: {type: "number"},
		frames_duration: {type: "number"},
		frames_flickerOnDuration: {type: "number"},
		frames_flickerOffDuration: {type: "number"},
		frames_timeCreditTarget: {type: "number"},
		frames_timeCreditMarkerInterval: {type: "number"},
		frames_timeCreditLimitPerFrame: {type: "number"},
		frames_voice_volumeMultiplier: {type: "number"},
		frames_voice_speedMultiplier: {type: "string"},
		frames_imageFolder: {type: "string"},
		frames_audioFolder: {type: "string"},
		frames_videoFolder: {type: "string"},
		frames_pools: {items: {$ref: "FramePool"}},

		solver_enabled: {type: "boolean"},
		solver_minFlawFinds: {type: "number"},
		solver_markProgressBeforeCompletion: {type: "boolean"},
		solver_maxTimeForProgress: {type: "number"},
	},
});

export class FramePool {
	constructor(data?: Partial<FramePool>) { Object.assign(this, data); }

	name = "";
	enabled = true;
	weight = 100; // relative weight, compared to other pools
	lockIn_min = 1;
	lockIn_max = 1;
	lockIn_forceSwitchAfter = false;
	mediaPathFilter = "";
	mediaPathExclude = "";
	characters: FrameCharacter[] = [];

	audioClip_enabled = false;
	audioClip_duration = 5; // in seconds
	audioClip_volumeMultiplier = 1;

	videoClip_enabled = false;
	videoClip_duration = 5; // in seconds
	videoClip_volumeMultiplier = 1;
	videoClip_audioTrack_preferredLanguage = "English";

	dreamClip_enabled = false;

	flaw_type = ImageFrameFlawType.wrongAudioClipPlayed;
	flaw_percent = .5;
}
AddSchema("FramePool", {
	properties: {
		name: {type: "string"},
		enabled: {type: "boolean"},
		weight: {type: "number"},
		lockIn_min: {type: "number"},
		lockIn_max: {type: "number"},
		lockIn_forceSwitchAfter: {type: "boolean"},
		mediaPathFilter: {type: "string"},
		mediaPathExclude: {type: "string"},
		characters: {items: {$ref: "FrameCharacter"}},

		audioClip_enabled: {type: "boolean"},
		audioClip_duration: {type: "number"},
		audioClip_volumeMultiplier: {type: "number"},

		videoClip_enabled: {type: "boolean"},
		videoClip_duration: {type: "number"},
		videoClip_volumeMultiplier: {type: "number"},
		videoClip_audioTrack_preferredLanguage: {type: "string"},

		dreamClip_enabled: {type: "boolean"},

		flawType: {$ref: "ImageFrameFlawType"},
		flawPercent: {type: "number"},
	},
});

export class FrameCharacter {
	constructor(data?: Partial<FrameCharacter>) { Object.assign(this, data); }
	initials: string;
	name_voicing: string;
	//voice_short: string;
	//voice_long: string;
	weight: number;
}
AddSchema("FrameCharacter", {
	properties: {
		initials: {type: "string"},
		name_voicing: {type: "string"},
		//voice_short: {type: "string"},
		//voice_long: {type: "string"},
		weight: {type: "number"},
	},
});